// Data layer for the team-library view. One cookie-authed GET /api/library.

export interface LibraryItem {
  shareId: string; songId: string | null; songName: string | null;
  ownerEmail: string | null; createdAt: string; lastSetlistDate?: string | null;
  /**
   * Владелец пометил ЭТУ запись главной для своей песни
   * (`shares.is_song_default`, 0019). Отсутствие — «не главная», и тогда
   * читатель открывает самую свежую, как и до волны F.
   *
   * НЕОБЯЗАТЕЛЬНОЕ — по той же причине, что `setlists?`/`songTexts?` у
   * `TeamLibrary` ниже, и причина эта не стилистическая: воркер и веб
   * выкатываются порознь, а тело ответа разбирается без строгой схемы. Ответ
   * воркера, который про метку ещё не знает, обязан читаться как «главной
   * нет» — и читается, потому что `undefined` здесь ложно, ровно как
   * `false`. Поле, помеченное обязательным, сделало бы такой ответ
   * недопустимым, чем он не является.
   */
  isSongDefault?: boolean;
}
export interface SetlistSummary {
  setlistId: string; name: string; ownerEmail: string | null; itemCount: number;
  createdAt: string; updatedAt: string; eventDate: string | null;
  /**
   * Живая ссылка слов ТОГО ЖЕ сетлиста (единый сетлист, фаза 1) — воркер
   * склеивает пару по ключу (владелец, local_setlist_id)
   * (team-setlists-merge.ts, `keyOf`), а не по составу песен, и отдаёт готовый
   * id, не оставляя веб самому гадать совпадение.
   *
   * НЕОБЯЗАТЕЛЬНОЕ — по той же причине, что `isSongDefault` у `LibraryItem` и
   * `setlists?`/`songTexts?` ниже: воркер и веб выкатываются порознь, и ответ
   * воркера, который про склейку ещё не знает, обязан читаться как «ссылки
   * слов нет» — `undefined` сводится к тому же `null`, что и явное отсутствие.
   */
  textShareId?: string | null;
}
/**
 * Ссылка слов БЕЗ сетлиста со звуком (единый сетлист, фаза 1) — второй сорт
 * строк вкладки «Сетлисты» рядом с `SetlistSummary`. `songIds` — состав
 * сетлиста порядком навигации, тот же порядок, что и `payload.songs` (см.
 * `setlist-screen/model.ts`).
 */
export interface TextSetlistSummary {
  textShareId: string; name: string; ownerEmail: string | null; itemCount: number;
  songIds: string[]; createdAt: string; eventDate: string | null;
  /**
   * Id сетлиста того же (владелец, local_setlist_id) — единый сетлист, фазы
   * 3+4, задача D4 (пробел, найденный задачей C5: `offline/setlist-offline.ts`
   * до этого поля не могла докачивать состав слов-только строк, только
   * сетлисты со звуком). Воркер (team-setlists-merge.ts) считает его границей
   * команды: строка ЭТОЙ же команды и не отозвана — иначе поле отсутствует.
   *
   * НЕОБЯЗАТЕЛЬНОЕ — по той же причине, что `textShareId?` у `SetlistSummary`
   * выше: старый воркер поле не знает, `undefined` читается как «сетлиста
   * нет», ровно как отсутствие.
   */
  setlistId?: string;
}
export interface SongTextSummary {
  id: string;
  /** Локальный id песни на десктопе владельца — ключ, по которому
   *  `buildSongRows` (ui/song-rows.ts) сшивает эту публикацию слов с
   *  аудио-версиями той же песни (`LibraryItem.songId`), а не по имени.
   *  Переименование песни после публикации слов, или две разные песни с
   *  одинаковым названием, ломали сшивку по имени молча — id этого не
   *  делает. */
  localSongId: string;
  songName: string;
  /** null — обычное состояние: тональность необязательна при публикации, а
   *  не угловой случай. Потребитель ОБЯЗАН это учитывать (например,
   *  `song.songKey?.toUpperCase()`, не `song.songKey.toUpperCase()`). */
  songKey: string | null;
  ownerEmail: string | null;
  updatedAt: string;
  /** null — песню ещё никто не открывал. */
  lastOpenedAt: string | null;
  /**
   * Кто видит СЛОВА этой песни (0019, волна F). Отражается переключателем в
   * листе управления песней — отражается, а не угадывается, поэтому состояние
   * едет сюда тем же ответом `/api/library`, которым рисуется список.
   *
   * НЕОБЯЗАТЕЛЬНОЕ — по той же причине, что `isSongDefault` выше и
   * `setlists?`/`songTexts?` у `TeamLibrary` ниже: воркер и веб выкатываются
   * порознь, а тело ответа разбирается без строгой схемы. Ответ воркера,
   * который про видимость ещё не знает, обязан читаться как «только команда» —
   * и читается, потому что отсутствие сводится к `'team'` у потребителя.
   *
   * Сводить ОБЯЗАНО быть сравнением с `'public'`, а не с `'team'`: проверка
   * наоборот прочитала бы `undefined` как «публично» и показала бы владельцу
   * публичной каждую песню библиотеки. Та же ловушка, что на стороне воркера
   * (`SongTextRow.visibility`), и тот же ответ на неё.
   */
  visibility?: 'team' | 'public';
}
// setlists?/songTexts? are optional for the same reason: web and desktop read
// this response but ship on separate schedules (desktop is a separate build
// that can lag behind or run ahead of the worker), and the client parses the
// body without strict schema validation. An additively-added field an old
// build doesn't know about is simply ignored; a field marked required would
// make that old build stumble over its absence.
export interface TeamLibrary { teamId: string; teamName: string; items: LibraryItem[]; setlists?: SetlistSummary[]; songTexts?: SongTextSummary[]; textSetlists?: TextSetlistSummary[]; }
export type LibraryResult =
  | { ok: true; teams: TeamLibrary[] }
  | { ok: false; needsAuth?: boolean };

export async function fetchLibrary(apiBase = '', fetchImpl: typeof fetch = fetch): Promise<LibraryResult> {
  try {
    const res = await fetchImpl(`${apiBase}/api/library`, { credentials: 'include' });
    if (res.status === 401) return { ok: false, needsAuth: true };
    if (!res.ok) return { ok: false };
    const body = await res.json() as any;
    return { ok: true, teams: Array.isArray(body?.teams) ? body.teams : [] };
  } catch {
    return { ok: false };
  }
}
